import React from 'react';
import Layout from '../components/Layout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { Container, Card, Alert, Row, Col } from 'react-bootstrap';

const PostingRules: React.FC = () => {
  const isMobile = useIsMobile();

  const content = (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Body className="p-4 p-md-5">
          <h1 className="mb-4">Ad Posting Rules</h1>

          <Alert variant="danger" className="mb-4">
            <p className="mb-0 small">
              NayaBajar does not allow posting ads that are illegal as per Nepalese law and/or
              violate the rules below. Users found violating these rules will face immediate
              action including account suspension or permanent ban.
            </p>
          </Alert>

          {/* Rule 1: Multiple Ads */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">1. Multiple (Duplicate/Repeat) Ad Posting</h2>
                <Alert variant="info" className="border mb-3">
                  <p className="fw-bold mb-2">
                    Users are NOT allowed to post more than ONE similar active ad.
                  </p>
                  <p className="small mb-0">
                    Similar active ads are defined as ads selling or looking for the same product
                    or service, even if they contain different text/images or are posted from
                    different accounts.
                  </p>
                </Alert>
                <p className="mb-2">
                  Each user can only post a similar ad if the previous one has been:
                </p>
                <ul>
                  <li>Marked as sold</li>
                  <li>Deleted by the user</li>
                  <li>Expired</li>
                </ul>
              </Col>
            </Row>
          </section>

          {/* Rule 2: Misleading Ads */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-warning">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">2. Misleading and Fraudulent Ads</h2>
                <p className="mb-3">The following are NOT allowed:</p>

                <div className="d-flex flex-column gap-3">
                  <Card bg="light" className="border-0">
                    <Card.Body className="p-3">
                      <h3 className="h6 fw-bold mb-2">Title & Price Mismatch</h3>
                      <p className="small mb-0">
                        Ad title, price, and content must be related. For example, posting "iPhone 15"
                        with a price of NPR 200 is not allowed.
                      </p>
                    </Card.Body>
                  </Card>

                  <Card bg="light" className="border-0">
                    <Card.Body className="p-3">
                      <h3 className="h6 fw-bold mb-2">Title Violations</h3>
                      <ul className="small mb-2">
                        <li>Titles should NOT include price or phone numbers (use separate fields)</li>
                        <li>No marketing gimmick words to grab attention</li>
                      </ul>
                      <p className="small text-muted fst-italic mb-0">
                        Examples: "Best opportunity of your life", "Click to earn money",
                        "Call me soon", "Don't miss out"
                      </p>
                    </Card.Body>
                  </Card>

                  <Card bg="light" className="border-0">
                    <Card.Body className="p-3">
                      <h3 className="h6 fw-bold mb-2">Inaccurate Information</h3>
                      <ul className="small mb-0">
                        <li>Wrong or misleading prices</li>
                        <li>Incorrect item condition (e.g., "Brand New" for used items)</li>
                        <li>Not disclosing defects or damage</li>
                      </ul>
                    </Card.Body>
                  </Card>

                  <Card bg="light" className="border-0">
                    <Card.Body className="p-3">
                      <h3 className="h6 fw-bold mb-2">Prohibited Items</h3>
                      <ul className="small mb-0">
                        <li>Stolen or illegal goods</li>
                        <li>Fake, replica, or counterfeit items presented as original</li>
                      </ul>
                    </Card.Body>
                  </Card>

                  <Card bg="light" className="border-0">
                    <Card.Body className="p-3">
                      <h3 className="h6 fw-bold mb-2">External Links & Downloads</h3>
                      <p className="small mb-0">
                        Ads with the sole purpose of leading users to another website or generate
                        downloads without actual product/service sale are not allowed.
                      </p>
                    </Card.Body>
                  </Card>

                  <Card bg="light" className="border-0">
                    <Card.Body className="p-3">
                      <h3 className="h6 fw-bold mb-2">Inappropriate Images</h3>
                      <ul className="small mb-0">
                        <li>Images unrelated to ad content</li>
                        <li>Graphic, violent, or adult-oriented images</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          </section>

          {/* Rule 3: Wrong Category */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px', backgroundColor: 'rgba(111, 66, 193, 0.1)'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#6f42c1'}}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">3. Mis-categorized (Wrong Category) Ads</h2>
                <p className="mb-3">
                  Ads MUST be posted in the correct category. For example:
                </p>

                <Alert variant="info" className="border mb-3" style={{backgroundColor: 'rgba(111, 66, 193, 0.1)'}}>
                  <ul className="mb-0">
                    <li>
                      Posting a Bike ad in Mobile category = Mis-categorized
                    </li>
                    <li>
                      Posting an office table in Home Furniture category = May be acceptable,
                      but Office Furniture category is better
                    </li>
                  </ul>
                </Alert>

                <Alert variant="primary" className="border">
                  <p className="fw-bold mb-1">💡 Not sure?</p>
                  <p className="small mb-0">
                    Search for existing similar ads to see where other users have posted them.
                    If your ad fits multiple categories, choose the best one and post only there.
                  </p>
                </Alert>
              </Col>
            </Row>
          </section>

          {/* Rule 4: Illegal/Obscene Content */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-danger">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">4. Illegal or Morally Obscene Ads</h2>
                <p className="mb-3">
                  NayaBajar does NOT allow ads that are illegal per Nepal law or considered
                  morally obscene in Nepalese society.
                </p>

                <div className="d-flex flex-column gap-2">
                  <Alert variant="danger" className="border">
                    <h3 className="h6 fw-bold mb-2">
                      Adult/Obscene Content
                    </h3>
                    <ul className="small mb-0">
                      <li>Prostitution or escort services</li>
                      <li>Obscene items or adult entertainment</li>
                      <li>Indecent massage services</li>
                      <li>Pornography or sexual items</li>
                    </ul>
                  </Alert>

                  <Alert variant="danger" className="border">
                    <h3 className="h6 fw-bold mb-2">
                      Offensive Content
                    </h3>
                    <p className="small mb-0">
                      Ads that hurt sentiments of any person, group, or religion
                    </p>
                  </Alert>

                  <Alert variant="danger" className="border">
                    <h3 className="h6 fw-bold mb-2">
                      Illegal Items
                    </h3>
                    <ul className="small mb-0">
                      <li>Weapons (guns, knives, etc.)</li>
                      <li>Drugs or controlled substances</li>
                      <li>Human organs</li>
                      <li>Endangered animals or products</li>
                      <li>Prohibited antiques</li>
                      <li>Black marketing activities</li>
                    </ul>
                  </Alert>

                  <Alert variant="danger" className="border">
                    <h3 className="h6 fw-bold mb-2">
                      Rights Violations
                    </h3>
                    <p className="small mb-0">
                      Ads that infringe third parties' intellectual property, moral rights, or privacy
                    </p>
                  </Alert>
                </div>
              </Col>
            </Row>
          </section>

          {/* Penalties Section */}
          <section className="mb-4">
            <Alert variant="danger" className="border" style={{background: 'linear-gradient(to right, rgba(220, 53, 69, 0.1), rgba(253, 126, 20, 0.1))'}}>
              <h2 className="mb-3">
                ⚠️ Penalties for Violation
              </h2>
              <ul>
                <li><strong>First offense:</strong> Ad removal + warning</li>
                <li><strong>Second offense:</strong> Account suspension (7 days)</li>
                <li><strong>Third offense:</strong> Permanent account ban</li>
                <li><strong>Severe violations:</strong> Immediate permanent ban without warning</li>
              </ul>
            </Alert>
          </section>

          {/* Contact Section */}
          <div className="mt-4 pt-4 border-top">
            <h3 className="h5 mb-3">Questions?</h3>
            <p className="mb-2">
              If you're unsure whether your ad complies with our rules, please contact us before posting.
            </p>
            <a
              href="mailto:support@nayabajar.com"
              className="text-primary fw-bold"
            >
              support@nayabajar.com
            </a>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );

  return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout showSidebar={false}>{content}</Layout>;
};

export default PostingRules;
